const API_URL = import.meta.env.VITE_API_URL as string

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiRequest<T>(
  path: string,
  options: {
    method?: string
    body?: unknown
    token?: string
  } = {},
): Promise<T> {
  const { method = 'GET', body, token } = options

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  const responseBody = await response.json()

  if (!response.ok) {
    throw new ApiError(
      (responseBody as { error?: string }).error ?? 'Request failed',
      response.status,
      responseBody,
    )
  }

  return responseBody as T
}

// Bearer-authenticated GET that returns the response body as a Blob — used
// for admin downloads (PDFs, QR labels) where the binary needs to render in
// a new browser tab. Browser navigation via `<a href>` can't carry the
// Authorization header, so the SPA must fetch with auth and hand the
// caller a same-origin blob URL instead.
export async function apiBlob(path: string, options: { token?: string } = {}): Promise<Blob> {
  const { token } = options

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, { method: 'GET', headers })

  if (!response.ok) {
    // Errors are JSON per the API contract. Fall back to text/empty if the
    // body isn't parseable so we still throw a typed ApiError.
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      body = null
    }
    throw new ApiError(
      (body as { error?: string } | null)?.error ?? 'Request failed',
      response.status,
      body,
    )
  }

  return await response.blob()
}
